/* eslint-disable no-throw-literal */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import {
  PAS_STATUS,
  POLL_RETRIES,
  PAYMENT_METHODS_POLL_RETRIES,
  PAS_FAILURE_ERRORS,
  getPollRetryDelay,
  getPaymentMethodsPollRetryDelay,
} from 'constants/paymentsSpa';
import paymentsSpaActions from 'redux/paymentsSpa/actions';
import { ddLogWithContext } from 'utils/errorLoggerUtil';
import useTranslation from 'utils/hooks/useTranslation';
import notify, { notifyErrorWithWorkflowId } from 'utils/notificationUtils';
import paymentsSpa from '../api/paymentsSpa';
import { paymentQueryKeys } from './queryKeys';
import useOnboardingProgressBar from 'components/Shared/OnboardingProgressBar/useOnboardingProgressBar';
import { getMaximumPaymentAccounts } from 'constants.js';
import { CORPORATE_CLIENT_RESTRICTION_TYPES } from 'constants/corporateClients';

const usePollPayments = ({ id, workflowId }) => {
  const { translateText } = useTranslation();
  const { onGetOnboardingProgress } = useOnboardingProgressBar();
  const [orderInitiated, setOrderInitiated] = React.useState('');
  const [scaChallenge, setScaChallenge] = React.useState('');

  React.useEffect(() => {
    if (orderInitiated) {
      ddLogWithContext({
        type: 'info',
        message: `Payment creation status changed (${PAS_STATUS.InitiateAuthentication}). Attaching hidden iframe.`,
        context: {
          id,
          workflowId,
        },
      });
    }
  }, [id, workflowId, orderInitiated]);

  React.useEffect(() => {
    if (scaChallenge) {
      ddLogWithContext({
        type: 'info',
        message: `Payment creation status changed (${PAS_STATUS.Challenged}). Attaching challenge iframe.`,
        context: {
          id,
          workflowId,
        },
      });
    }
  }, [id, workflowId, scaChallenge]);

  const pollPayments = React.useCallback(
    async ({ queryKey }) => {
      const [, paymentId, traceId] = queryKey;
      const { data } = await paymentsSpa.readOne(paymentId, traceId);
      const { creationStatus } = data;
      if (creationStatus.status === PAS_STATUS.Pending) {
        throw {
          status: PAS_STATUS.Pending,
          paymentId,
          workflowId: traceId,
        };
      }

      const { cardValidations } = creationStatus;
      if (creationStatus.status === PAS_STATUS.InitiateAuthentication) {
        const orderInitiated = cardValidations[0].initiateAuthentication.gatewayHtml;
        setOrderInitiated(atob(orderInitiated));
        throw {
          status: PAS_STATUS.InitiateAuthentication,
          paymentId,
          workflowId: traceId,
        };
      }

      if (creationStatus.status === PAS_STATUS.Challenged) {
        const challenge = cardValidations[0].challengeDetails.challengeQuestion;
        setScaChallenge(atob(challenge));
        throw {
          status: PAS_STATUS.Challenged,
          paymentId,
          workflowId: traceId,
        };
      }

      if (creationStatus.status === PAS_STATUS.Failed) {
        const reason =
          PAS_FAILURE_ERRORS[creationStatus.failureReason] || PAS_FAILURE_ERRORS.UnhandledError;
        ddLogWithContext({
          type: 'warn',
          message: `Payment creation failed with reason ${reason}. Exiting polling process.`,
          context: {
            id: paymentId,
            workflowId: traceId,
          },
        });
        throw {
          status: PAS_STATUS.Failed,
          reason,
          paymentId,
          workflowId: traceId,
        };
      }

      if (creationStatus.status === PAS_STATUS.Created) {
        ddLogWithContext({
          type: 'info',
          message: `Payment creation successful. Exiting polling process.`,
          context: {
            id: paymentId,
            workflowId: traceId,
          },
        });
        return {
          status: PAS_STATUS.Created,
          paymentId,
          workflowId: traceId,
        };
      }

      ddLogWithContext({
        type: 'warn',
        message: 'Payment polling process could not continue due to an unhandled error',
        context: {
          id: paymentId,
          workflowId: traceId,
        },
      });
      throw {
        status: PAS_STATUS.Failed,
        reason: PAS_FAILURE_ERRORS.UnhandledError,
        paymentId,
        workflowId: traceId,
      };
    },
    [setOrderInitiated, setScaChallenge],
  );

  const query = useQuery({
    queryFn: pollPayments,
    queryKey: ['pollPayments', id, workflowId],
    retry: (retryCount, error) => {
      if (retryCount > POLL_RETRIES) {
        ddLogWithContext({
          type: 'info',
          message: 'Payment polling process expired.',
          context: {
            id: error.paymentId,
            workflowId: error.workflowId,
          },
        });
        return false;
      }

      const status = error?.status;
      if (
        status === PAS_STATUS.Pending ||
        status === PAS_STATUS.InitiateAuthentication ||
        status === PAS_STATUS.Challenged
      ) {
        return true;
      }

      return false;
    },
    retryDelay: getPollRetryDelay,
    enabled: false,
    onSuccess: () => {
      notify({
        type: 'success',
        message: translateText('corporateAccounts.payments.create.success'),
      });
      onGetOnboardingProgress();
    },
    onError: error => {
      const mappedError = error.reason || PAS_FAILURE_ERRORS.UnhandledError;

      notifyErrorWithWorkflowId({
        errorCodeTitle: translateText('corporateAccounts.common.errorCode'),
        message: `${translateText(
          `corporateAccounts.payments.create.error.${mappedError}`,
        )} ${translateText('corporateAccounts.common.errorCode.copy')}`,
        workflowId: error.workflowId,
      });
    },
  });

  const pollReset = React.useCallback(() => {
    query.remove();
    setScaChallenge('');
    setOrderInitiated('');
  }, [setOrderInitiated, setScaChallenge, query]);

  return {
    startPolling: query.refetch,
    isPollSuccess: query.isSuccess,
    isPollError: query.isError,
    isPolling: query.isLoading,
    pollError: query.error,
    scaChallenge,
    orderInitiated,
    pollReset,
  };
};

// The payment methods list (corporate-accounts) lags behind PAS: right after a card is created, the
// first GET /payments often comes back without it. Re-read the list until the new payment account
// shows up, then hand the result to the paymentsSpa store the legacy list is rendered from.
export const useRefreshPaymentMethods = () => {
  const dispatch = useDispatch();
  const [paymentAccountId, setPaymentAccountId] = React.useState(null);

  const readPaymentMethods = React.useCallback(async ({ queryKey }) => {
    const [, createdPaymentAccountId] = queryKey;
    const { response } = await paymentsSpa.read();
    const paymentMethods = response?.data || [];

    const hasCreatedCard = paymentMethods.some(
      paymentMethod =>
        paymentMethod.paymentAccountId?.toLowerCase() === createdPaymentAccountId?.toLowerCase(),
    );

    if (!hasCreatedCard) {
      throw {
        paymentAccountId: createdPaymentAccountId,
        paymentMethods,
      };
    }

    return paymentMethods;
  }, []);

  const query = useQuery({
    queryFn: readPaymentMethods,
    queryKey: [paymentQueryKeys.getPaymentMethodsQuery, paymentAccountId],
    enabled: false,
    cacheTime: 0,
    retry: retryCount => retryCount < PAYMENT_METHODS_POLL_RETRIES,
    retryDelay: getPaymentMethodsPollRetryDelay,
    onSuccess: paymentMethods => {
      dispatch({ type: paymentsSpaActions.GET_SUCCESS, payload: paymentMethods });
    },
    onError: error => {
      ddLogWithContext({
        type: 'warn',
        message: 'Payment account is still missing from the payment methods list. Giving up.',
        context: {
          id: error?.paymentAccountId,
          retries: PAYMENT_METHODS_POLL_RETRIES,
        },
      });

      // The list itself was readable, it just does not hold the new card yet — show what it returned
      // rather than leaving the Select spinning or in an error state.
      if (error?.paymentMethods) {
        dispatch({ type: paymentsSpaActions.GET_SUCCESS, payload: error.paymentMethods });
      } else {
        dispatch({ type: paymentsSpaActions.GET_ERROR });
      }
    },
  });

  const { refetch } = query;

  React.useEffect(() => {
    if (paymentAccountId) {
      dispatch({ type: paymentsSpaActions.SET_STATE, payload: { loading: true, getError: false } });
      refetch();
    }
  }, [paymentAccountId, dispatch, refetch]);

  return {
    refreshPaymentMethods: setPaymentAccountId,
    isRefreshingPaymentMethods: query.isFetching,
  };
};

export const useCreateSharedPayment = ({ onReset }) => {
  const { translateText } = useTranslation();
  const [pollingDetails, setPollingDetails] = React.useState({ id: null, workflowId: null });
  const {
    startPolling,
    isPolling,
    isPollSuccess,
    isPollError,
    pollError,
    scaChallenge,
    orderInitiated,
    pollReset,
  } = usePollPayments(pollingDetails);
  const createPayment = async ({ card, restrictionType }) => {
    if (restrictionType?.toLowerCase() === CORPORATE_CLIENT_RESTRICTION_TYPES.RESTRICTED) {
      const {
        response: { data: availablePaymentMethods },
      } = await paymentsSpa.read();
      if (availablePaymentMethods.length >= getMaximumPaymentAccounts())
        throw {
          status: PAS_STATUS.Failed,
          reason: PAS_FAILURE_ERRORS.RestrictedCorporateClientTotalLimitExceeded,
        };
    }

    const { response, workflowId } = await paymentsSpa.create(card);
    return {
      data: response?.data,
      workflowId,
    };
  };

  useEffect(() => {
    if (pollingDetails.id && pollingDetails.workflowId) {
      ddLogWithContext({
        type: 'info',
        message: `Payment creation initiated. Starting polling process for payment ID ${pollingDetails.id}.`,
        context: {
          ...pollingDetails,
        },
      });
      startPolling();
    }
  }, [pollingDetails, startPolling]);

  useEffect(() => {
    return () => {
      setPollingDetails({ id: null, workflowId: null });
    };
  }, [setPollingDetails]);

  const mutation = useMutation({
    mutationFn: createPayment,
    mutationKey: 'createPayment',
    onSuccess: ({ data, workflowId }) => {
      setPollingDetails({
        id: data.creationStatus.paymentAccountId,
        workflowId,
      });
    },
    onError: error => {
      const mappedError = error.reason || PAS_FAILURE_ERRORS.UnhandledError;

      notifyErrorWithWorkflowId({
        errorCodeTitle: translateText('corporateAccounts.common.errorCode'),
        message: `${translateText(
          `corporateAccounts.payments.create.error.${mappedError}`,
        )} ${translateText('corporateAccounts.common.errorCode.copy')}`,
        workflowId: error.workflowId,
      });
    },
  });
  return {
    ...mutation,
    reset: () => {
      mutation.reset();
      pollReset();
      onReset();
    },
    isLoading: mutation.isLoading || isPolling,
    isSuccess: isPollSuccess,
    paymentAccountId: pollingDetails.id,
    isError: isPollError,
    pollError,
    scaChallenge,
    orderInitiated,
  };
};
